/**
 * 手機全螢幕按鈕(boss 2026-06-13「手機的瀏覽器讓他沒辦法全屏」)。
 *
 * 問題:iPhone Safari 在一般分頁開網頁時,頂部網址列 + 底部工具列會吃掉畫面,
 * 遊戲頂部(戰力槽 / Wave)被擋住、感覺打不到。手機瀏覽器各家全螢幕能力不同:
 *  - Android Chrome / 桌機:支援 Fullscreen API → 點按鈕直接真全螢幕(edge-to-edge)。
 *  - iOS Safari:**不**對一般網頁開放 Fullscreen API(只有 <video> 例外)。唯一真
 *    全螢幕途徑 = 「分享 → 加入主畫面」,從主畫面以 standalone 啟動(manifest
 *    display:standalone + apple-mobile-web-app-capable 已設)。故 iOS 改顯示
 *    A2HS 教學 overlay,而非試圖呼叫不存在的 API。
 *  - 已是 standalone(從主畫面開)/ 已在全螢幕 → 不顯示按鈕(已經滿屏)。
 *
 * 搭配 index.html 的 100dvh:即使留在瀏覽器分頁,畫布也貼齊「實際可見」區域,
 * 不會被工具列裁掉頂部;這顆按鈕是「想要更滿」時的進一步入口。
 *
 * 決策邏輯抽成純函式 decideFullscreenAction(env) 方便單元測試;DOM 掛載讀真實
 * 環境後套用。掛在 #game-container 內 → 跟著 setMode display 切換,學習端看不到。
 * 鐵則:純 DOM,0 題目、0 戰鬥數值。
 */

const STYLE_ID = 'ef-fullscreen-style';
const BTN_ID = 'ef-fullscreen-btn';
const TIP_ID = 'ef-fullscreen-tip';

export type FullscreenEnv = {
  /** 已是 PWA standalone(從主畫面開)或已在全螢幕。 */
  standalone: boolean;
  /** 瀏覽器支援 Element.requestFullscreen(含 webkit 前綴)。 */
  fullscreenSupported: boolean;
  /** iOS / iPadOS 裝置(Fullscreen API 對網頁不可用,改走加入主畫面)。 */
  ios: boolean;
};

/** 按鈕該做什麼。'hidden' = 不顯示按鈕(已滿屏或無從改善)。 */
export type FullscreenAction = 'hidden' | 'native-fullscreen' | 'ios-a2hs-tip';

/**
 * 依環境決定全螢幕按鈕行為(純函式)。
 * 順序:已 standalone → 藏;支援原生全螢幕 → 原生;iOS → 加入主畫面教學;
 * 其餘(罕見的舊裝置,既不支援又非 iOS)→ 藏(沒有可行途徑,不顯示死按鈕)。
 */
export function decideFullscreenAction(env: FullscreenEnv): FullscreenAction {
  if (env.standalone) return 'hidden';
  if (env.fullscreenSupported) return 'native-fullscreen';
  if (env.ios) return 'ios-a2hs-tip';
  return 'hidden';
}

function detectStandalone(): boolean {
  try {
    const navAny = navigator as unknown as { standalone?: boolean };
    if (navAny.standalone === true) return true;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
  } catch { /* matchMedia/navigator 不可用 → 視為非 standalone */ }
  return false;
}

function detectIOS(): boolean {
  try {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ 偽裝成 Mac:Macintosh + 多點觸控 = iPad。
    if (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return true;
  } catch { /* ua 不可用 */ }
  return false;
}

function detectFullscreenSupported(): boolean {
  try {
    const el = document.documentElement as unknown as {
      requestFullscreen?: unknown; webkitRequestFullscreen?: unknown;
    };
    return typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function';
  } catch { return false; }
}

function isCurrentlyFullscreen(): boolean {
  try {
    const d = document as unknown as { fullscreenElement?: unknown; webkitFullscreenElement?: unknown };
    return !!(d.fullscreenElement || d.webkitFullscreenElement);
  } catch { return false; }
}

function requestNativeFullscreen(): void {
  try {
    const el = document.documentElement as unknown as {
      requestFullscreen?: () => Promise<void>; webkitRequestFullscreen?: () => void;
    };
    if (typeof el.requestFullscreen === 'function') { void el.requestFullscreen(); }
    else if (typeof el.webkitRequestFullscreen === 'function') { el.webkitRequestFullscreen(); }
  } catch { /* 使用者手勢外呼叫會被拒,靜默 */ }
}

function exitNativeFullscreen(): void {
  try {
    const d = document as unknown as {
      exitFullscreen?: () => Promise<void>; webkitExitFullscreen?: () => void;
    };
    if (typeof d.exitFullscreen === 'function') { void d.exitFullscreen(); }
    else if (typeof d.webkitExitFullscreen === 'function') { d.webkitExitFullscreen(); }
  } catch { /* noop */ }
}

const CSS = `
#${BTN_ID} {
  position: absolute;
  top: max(8px, env(safe-area-inset-top)); left: max(8px, env(safe-area-inset-left));
  z-index: 55;
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; line-height: 1; color: #ffd54f;
  background: rgba(16, 19, 30, .62); border: 1px solid rgba(255, 213, 79, .5);
  cursor: pointer; touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
#${BTN_ID}:active { background: rgba(16, 19, 30, .9); }
#${TIP_ID} {
  position: absolute; inset: 0; z-index: 70; display: flex;
  flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; padding: 28px; box-sizing: border-box; text-align: center;
  background: linear-gradient(168deg, #0c101c 0%, #18203a 52%, #0c101c 100%);
  color: #e9e6da;
}
#${TIP_ID} h3 { margin: 0; font-size: 19px; color: #ffd54f; letter-spacing: 1px; }
#${TIP_ID} ol { margin: 4px 0 0; padding-left: 0; list-style: none;
  font-size: 15px; line-height: 1.9; opacity: .92; }
#${TIP_ID} .ef-fs-tip__close {
  margin-top: 8px; padding: 10px 24px; font-size: 14px; color: #cfd6e6;
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.22);
  border-radius: 999px; cursor: pointer; touch-action: manipulation;
}
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function showIOSTip(host: HTMLElement): void {
  if (document.getElementById(TIP_ID)) return;
  const tip = document.createElement('div');
  tip.id = TIP_ID;
  tip.innerHTML = `
    <h3>讓遊戲全螢幕</h3>
    <ol>
      <li>① 點 Safari 底部的「分享」⬆️</li>
      <li>② 選「加入主畫面」</li>
      <li>③ 從主畫面的圖示開啟</li>
    </ol>
    <p style="margin:0;font-size:13px;opacity:.7;">從主畫面開啟就沒有網址列,整個畫面都是遊戲。</p>
    <button type="button" class="ef-fs-tip__close">知道了</button>
  `;
  tip.querySelector('.ef-fs-tip__close')?.addEventListener('click', () => tip.remove());
  host.appendChild(tip);
}

/**
 * Mount the fullscreen button into the game container. Idempotent. Returns an
 * unmount function. Reads the live environment to decide whether/how to show.
 */
export function mountFullscreenButton(container?: HTMLElement | null): () => void {
  const host = container ?? document.getElementById('game-container');
  if (!host) return () => { /* no container (test harness) */ };
  if (document.getElementById(BTN_ID)) {
    const existing = document.getElementById(BTN_ID)!;
    return () => existing.remove();
  }

  const action = decideFullscreenAction({
    standalone: detectStandalone(),
    fullscreenSupported: detectFullscreenSupported(),
    ios: detectIOS(),
  });
  if (action === 'hidden') return () => { /* nothing to mount */ };

  ensureStyle();
  const btn = document.createElement('button');
  btn.id = BTN_ID;
  btn.type = 'button';
  btn.setAttribute('aria-label', '全螢幕');
  btn.textContent = '⛶';
  btn.addEventListener('click', () => {
    if (action === 'native-fullscreen') {
      if (isCurrentlyFullscreen()) exitNativeFullscreen();
      else requestNativeFullscreen();
    } else if (action === 'ios-a2hs-tip') {
      showIOSTip(host);
    }
  });
  host.appendChild(btn);
  return () => {
    btn.remove();
    document.getElementById(TIP_ID)?.remove();
  };
}
